/**
 * useCurrentUser — fetches GET /me. Loaded once auth is ready.
 */
import { useQuery } from '@tanstack/react-query';
import { CurrentUserSchema, type CurrentUser } from '@vari-crew/schemas';
import { ApiError, apiCall } from '@/lib/api';
import { useAuth } from './AuthProvider';

export function useCurrentUser() {
  const { isAuthenticated, mode, mockRole } = useAuth();
  return useQuery<CurrentUser>({
    queryKey: ['me', mode, mockRole],
    enabled: isAuthenticated,
    staleTime: 60_000,
    queryFn: async () => {
      // Time out a hung request (e.g. the API restarting mid-deploy) so the
      // query retries instead of the app spinning on a held connection.
      const raw = await apiCall('/me', { timeoutMs: 12_000 });
      return CurrentUserSchema.parse(raw);
    },
    // Ride out a deploy/restart window: keep retrying timeouts / network / 5xx
    // with capped backoff, but never hammer a real 4xx (auth / access codes).
    retry: (failureCount, error) =>
      error instanceof ApiError && error.status >= 400 && error.status < 500
        ? false
        : failureCount < 12,
    retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 8_000),
  });
}
